import { rawApi } from "@/lib/offline/net";
import {
  cachePut, cacheGet, blobPut, blobGet, blobDel,
  outboxAdd, outboxAll, outboxDel, kvGet, kvPut,
} from "@/lib/offline/db";
import { setStatus } from "@/lib/offline/store";

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}${Math.random().toString(16).slice(2)}`);
const tmp = () => `tmp_${uid()}`;
let seq = Date.now();
const nextSeq = () => ++seq;

// axios error with no `response` == the request never reached the server (offline / DNS / timeout)
const isNetworkError = (e) => !!e && !e.response;
function offlineError() { const e = new Error("You're offline"); e.__offline = true; return e; }
const hasTmp = (s) => String(s).includes("tmp_");

// Endpoints that make no sense to queue — fail fast so the UI shows an error.
const ONLINE_ONLY = /(\/billing\/)|(\/share$)|(sort-by-trade)|(\/dispatch$)|(\/og$)|(\/auth\/(login|signup|google|logout))/;
// Creates we assign a temp id to (so later ops can reference them offline).
const RX_JOB = /^\/jobs$/;
const RX_REPORT = /^\/jobs\/[^/]+\/reports$/;
const RX_ITEM = /^\/reports\/[^/]+\/items$/;

/* ---------- temp-id remap ---------- */
let remap = {};
async function loadRemap() { remap = (await kvGet("remap")) || {}; }
async function saveRemap() { await kvPut("remap", remap); }
function subStr(s) { if (!s) return s; let out = String(s); for (const [t, r] of Object.entries(remap)) out = out.split(t).join(r); return out; }
function subObj(o) { if (o == null) return o; try { return JSON.parse(subStr(JSON.stringify(o))); } catch { return o; } }

async function refreshPending() { const all = await outboxAll(); setStatus({ pending: all.length }); return all; }

/* ---------- optimistic cache mutations (so offline GETs reflect new data) ---------- */
async function cacheUnshift(url, item) { const cur = (await cacheGet(url)) || []; if (Array.isArray(cur)) { cur.unshift(item); await cachePut(url, cur); } }
async function cacheAddReport(jobUrl, report) {
  const cur = await cacheGet(jobUrl);
  if (cur && Array.isArray(cur.reports)) {
    cur.reports.unshift({
      ...report,
      stats: { token: null, url: null, total: 0, views: [] },
      progress: { total: 0, done: 0, verified: 0, pending_review: 0, pct: 0, photo_count: 0 },
      archived: false, thumb_url: null,
    });
    await cachePut(jobUrl, cur);
  }
}
async function cacheAddItem(reportUrl, item) { const cur = await cacheGet(reportUrl); if (cur && Array.isArray(cur.items)) { cur.items.push(item); await cachePut(reportUrl, cur); } }
async function minimalJob(jobId) {
  const jc = await cacheGet(`/jobs/${jobId}`);
  if (jc) return { id: jobId, name: jc.name, location: jc.location || "" };
  const list = await cacheGet("/jobs");
  const f = Array.isArray(list) ? list.find((j) => j.id === jobId) : null;
  return f ? { id: jobId, name: f.name, location: f.location || "" } : { id: jobId, name: "Job", location: "" };
}

/* ---------- enqueue ---------- */
async function enqueue(entry) {
  entry.id = entry.id || uid();
  entry.seq = nextSeq();
  entry.createdAt = Date.now();
  await outboxAdd(entry);
  await refreshPending();
  flush();
  return entry;
}

/* ---------- public: wrapped GET ---------- */
export async function wrappedGet(url, config) {
  // Virtual (not-yet-synced) entities live only in the local cache.
  if (String(url).includes("tmp_")) {
    const c = await cacheGet(url);
    if (c !== undefined) return { data: c, __offline: true };
  }
  try {
    const res = await rawApi.get(url, config);
    cachePut(url, res.data);
    return res;
  } catch (e) {
    if (isNetworkError(e)) {
      const c = await cacheGet(url);
      if (c !== undefined) return { data: c, __offline: true };
    }
    throw e;
  }
}

/* ---------- public: cache-first GET (stale-while-revalidate) ---------- */
// Returns cached data instantly (if present) and refreshes in the background,
// calling onFresh(freshData) when the network responds. Falls back to cache offline.
export async function wrappedGetCached(url, onFresh, config) {
  if (String(url).includes("tmp_")) {
    const c = await cacheGet(url);
    return { data: c, __cached: c !== undefined };
  }
  const cached = await cacheGet(url);
  const fetchFresh = async () => {
    try {
      const res = await rawApi.get(url, config);
      cachePut(url, res.data);
      if (onFresh) onFresh(res.data);
      return res.data;
    } catch (e) {
      if (isNetworkError(e) && cached !== undefined) return cached;
      throw e;
    }
  };
  if (cached !== undefined) {
    fetchFresh().catch(() => {});   // revalidate in background, don't block paint
    return { data: cached, __cached: true };
  }
  return { data: await fetchFresh(), __cached: false };
}

/* ---------- public: wrapped mutate (post/patch/delete) ---------- */
export async function wrappedMutate(method, url, body, config) {
  const online = navigator.onLine;
  // Only defer to the outbox when a request actually depends on a not-yet-synced
  // temp id. Everything else, when online, writes straight to the server — so a
  // new job/report/item is never trapped in the queue behind unrelated work.
  const dependsOnQueued = hasTmp(url) || hasTmp(JSON.stringify(body || ""));

  if (online && !dependsOnQueued) {
    try {
      return method === "delete" ? await rawApi.delete(url, config) : await rawApi[method](url, body, config);
    } catch (e) {
      if (!isNetworkError(e)) throw e;   // genuine server/client error → surface it
      // network dropped mid-request → fall through and queue (best effort)
    }
  }
  // Offline, or the request references a queued temp id.
  if (ONLINE_ONLY.test(url)) throw offlineError();   // payments/share/etc. can't be queued
  return enqueueMutation(method, url, body);
}

async function enqueueMutation(method, url, body) {
  let optimistic = { ok: true };
  let producesId = null;

  if (method === "post" && RX_JOB.test(url)) {
    const id = tmp(); producesId = id;
    optimistic = { ...(body || {}), id, status: "open", report_count: 0, progress: 0, review_pct: 0, total_views: 0, _local: true };
    await cacheUnshift("/jobs", optimistic);
    await cachePut(`/jobs/${id}`, { ...optimistic, reports: [] });
  } else if (method === "post" && RX_REPORT.test(url)) {
    const id = tmp(); producesId = id;
    const jobId = url.split("/")[2];
    optimistic = { id, job_id: jobId, title: (body && body.title) || "New Report", status: "draft", _local: true };
    await cachePut(`/reports/${id}`, {
      report: optimistic, job: await minimalJob(jobId), items: [],
      stats: { token: null, url: null, total: 0, last_opened_at: null, last_country: null, views: [] },
    });
    await cacheAddReport(`/jobs/${jobId}`, optimistic);
  } else if (method === "post" && RX_ITEM.test(url)) {
    const id = tmp(); producesId = id;
    const reportId = url.split("/")[2];
    optimistic = { id, ...(body || {}), photos: [], _local: true };
    await cacheAddItem(`/reports/${reportId}`, optimistic);
  }

  await enqueue({ t: "json", method, url, body: body || null, producesId });
  return { data: optimistic };
}

/* ---------- public: queue a photo upload offline ---------- */
export async function enqueuePhoto({ endpoint, fields, fullBlob, thumbBlob, w, h }) {
  const fullId = uid();
  const thumbId = uid();
  await blobPut(fullId, fullBlob);
  await blobPut(thumbId, thumbBlob);
  const photoTmp = tmp();
  await enqueue({ t: "photo", endpoint, fields: fields || {}, w: w || 0, h: h || 0, fullId, thumbId, photoTmp });
  return {
    id: photoTmp, kind: (fields && fields.kind) || "reference", caption: (fields && fields.caption) || "",
    cloud_path: null, thumbnail_cloud_path: null,
    url: URL.createObjectURL(fullBlob), thumb_url: URL.createObjectURL(thumbBlob), _local: true,
  };
}

/* ---------- flush the outbox ---------- */
const MAX_AUTO_ATTEMPTS = 6;
let flushing = false;
let retryTimer = null;
function scheduleRetry(delay) { if (retryTimer) return; retryTimer = setTimeout(() => { retryTimer = null; flush(); }, delay); }

const statusOf = (err) => (err && err.response && err.response.status) || 0;
const isTransient = (err) => { const s = statusOf(err); return s >= 500 || s === 408 || s === 429; };

async function dropEntry(e) { if (e.t === "photo") { await blobDel(e.fullId); await blobDel(e.thumbId); } await outboxDel(e.id); }
async function dropDependents(tmpId) {
  const all = await outboxAll();
  for (const e of all) {
    const hay = e.t === "photo" ? JSON.stringify(e.fields || {}) + (e.endpoint || "") : JSON.stringify(e.body || "") + (e.url || "");
    if (hay.includes(tmpId)) await dropEntry(e);
  }
}

export async function flush(manual = false) {
  if (flushing || !navigator.onLine) return;
  const all = await outboxAll();
  if (!all.length) { setStatus({ syncing: false, pending: 0, stuck: false }); return; }
  flushing = true;
  setStatus({ syncing: true });
  await loadRemap();
  let stuck = false;
  try {
    for (const e of all) {
      // An entry that already exhausted auto-retries waits for an explicit manual retry.
      if (!manual && (e.attempts || 0) >= MAX_AUTO_ATTEMPTS) { stuck = true; break; }
      // Never send a payload that still carries an unresolved tmp_ id (producer not synced).
      const urlS = subStr(e.url || e.endpoint || "");
      const payloadS = e.t === "photo" ? subStr(JSON.stringify(e.fields || {})) : subStr(JSON.stringify(e.body || ""));
      if (hasTmp(urlS) || hasTmp(payloadS)) { stuck = true; break; }
      try {
        if (e.t === "json") {
          const url = subStr(e.url);
          const body = subObj(e.body);
          const res = e.method === "delete" ? await rawApi.delete(url) : await rawApi[e.method](url, body);
          if (e.producesId && res && res.data && res.data.id) { remap[e.producesId] = res.data.id; await saveRemap(); }
        } else if (e.t === "photo") {
          const endpoint = subStr(e.endpoint);
          const fields = subObj(e.fields) || {};
          const full = await blobGet(e.fullId);
          const thumb = await blobGet(e.thumbId);
          if (!full || !thumb) { await dropEntry(e); await refreshPending(); continue; }
          const fd = new FormData();
          Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
          fd.append("width", String(e.w || 0));
          fd.append("height", String(e.h || 0));
          fd.append("full", full, "full.webp");
          fd.append("thumb", thumb, "thumb.webp");
          const res = await rawApi.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (e.photoTmp && res && res.data && res.data.id) { remap[e.photoTmp] = res.data.id; await saveRemap(); }
          await blobDel(e.fullId);
          await blobDel(e.thumbId);
        }
        await outboxDel(e.id);
        await refreshPending();
      } catch (err) {
        if (isNetworkError(err)) break;                 // offline again — keep queue, retry later
        if (isTransient(err)) {                          // 5xx/timeout — DON'T drop; back off and retry
          e.attempts = (e.attempts || 0) + 1;
          await outboxAdd(e);
          if (e.attempts >= MAX_AUTO_ATTEMPTS) stuck = true;
          else scheduleRetry(Math.min(30000, 2000 * 2 ** (e.attempts - 1)));
          break;                                         // preserve FIFO order
        }
        // genuine client error (4xx) — safe to drop; cascade to anything depending on it
        await dropEntry(e);
        if (e.producesId) await dropDependents(e.producesId);
        await refreshPending();
        stuck = true;
      }
    }
  } finally {
    flushing = false;
    const left = await outboxAll();
    setStatus({ syncing: false, pending: left.length, stuck: left.length ? stuck : false });
    if (!left.length) { remap = {}; await saveRemap(); }
  }
}

// Manual "tap to retry" — clears the exhausted-attempt counters and flushes again.
export async function retryFailed() {
  const all = await outboxAll();
  for (const e of all) { if (e.attempts) { e.attempts = 0; await outboxAdd(e); } }
  setStatus({ stuck: false });
  flush(true);
}

/* ---------- init listeners (called once from api.js) ---------- */
let inited = false;
let flushTimer = null;
// Defer the flush briefly so a big queued upload doesn't saturate the connection
// while the app is still hydrating auth + the first screen on launch/resume.
function flushSoon(delay = 1500) {
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => { if (navigator.onLine) flush(); }, delay);
}
export function initOffline() {
  if (inited || typeof window === "undefined") return;
  inited = true;
  try { navigator.storage?.persist?.(); } catch { /* ignore */ }
  window.addEventListener("online", () => { setStatus({ online: true }); flush(); });
  window.addEventListener("offline", () => setStatus({ online: false }));
  document.addEventListener("visibilitychange", () => { if (!document.hidden) { setStatus({ online: navigator.onLine }); flushSoon(); } });
  setInterval(() => { if (navigator.onLine) flush(); }, 20000);
  refreshPending();
  if (navigator.onLine) flushSoon();
}
